/**
 * useResolvedBlock Hook
 *
 * Generic read-only hook for fetching resolved block data.
 * Works for any canvas block type (play, formation, concept).
 * Use this for rendering contexts where mutations aren't needed.
 *
 * For editor contexts with mutations, use usePlayBlocks instead.
 */

import { useQuery } from '@tanstack/react-query'
import type { ResolvedBlock } from '@/types/blocks'
import { secureFetch } from '@/utils/SecureFetch'

/**
 * Fetches resolved block data from the API.
 * Exported so usePlayBlocks can reuse the same fetch logic.
 */
export async function fetchResolvedBlock(blockId: string): Promise<ResolvedBlock> {
	const response = await secureFetch(`/api/blocks/${blockId}/resolved`)

	if (!response.ok) {
		const body = await response.text().catch(() => '')
		console.error(`[useResolvedBlock] Failed to fetch blockId=${blockId}: ${response.status}`, body)
		throw new Error(`Failed to fetch block: ${response.status} ${body}`)
	}

	const data = await response.json()
	return data.resolvedBlock
}

/**
 * Query key for resolved block data
 */
export const getResolvedBlockQueryKey = (blockId: string | null) =>
	['resolvedBlock', blockId] as const

/**
 * Generic read-only hook for fetching resolved block data.
 * Works for plays, formations, and concepts.
 *
 * @example
 * ```tsx
 * function BlockVisual({ block }) {
 *   const { resolvedBlock, isLoading } = useResolvedBlock(block.id)
 *   // Render block...
 * }
 * ```
 */
export function useResolvedBlock(blockId: string | null, enabled = true) {
	const shouldFetch = blockId !== null && enabled

	const { data: resolvedBlock, isLoading, error } = useQuery({
		queryKey: getResolvedBlockQueryKey(blockId),
		queryFn: () => fetchResolvedBlock(blockId!),
		enabled: shouldFetch,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	})

	return {
		resolvedBlock: resolvedBlock ?? null,
		isLoading: shouldFetch ? isLoading : false,
		error: error instanceof Error ? error : null,
	}
}
